import _ from 'lodash';
import { getSelectedCommunity } from '../../hooks/community/useSelectedCommunity';
import {
  deleteEmail,
  deleteNotification,
  setEmails,
  setNotifications,
  setPostcards,
  updateEmail,
  updateNotification,
} from '../../redux/slices/communicationsSlice';
import { store } from '../../redux/store';
import { PostcardOrder, ScheduledEmail, ScheduledNotification } from '../../types/communication';
import httpService from '../httpService';
import { RootState } from '../../redux/reducers';

function getEmails(): Promise<ScheduledEmail[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService<ScheduledEmail[]>(`/networks/dashboards/${selectedDashboard}/communication/email`).then(({ data }) => {
    store.dispatch(setEmails({ communityId: getSelectedCommunity()?.id!, emails: data }));
    return data;
  });
}

function getNotifications(): Promise<ScheduledNotification[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService<ScheduledNotification[]>(`/networks/dashboards/${selectedDashboard}/communication/notification`).then(
    ({ data }) => {
      store.dispatch(
        setNotifications({ communityId: getSelectedCommunity()?.id!, notifications: data }),
      );
      return data;
    },
  );
}

function getPostcards(): Promise<PostcardOrder[]> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService<PostcardOrder[]>(`/networks/dashboards/${selectedDashboard}/orders/postcards`).then(({ data }) => {
    store.dispatch(
      setPostcards({ communityId: getSelectedCommunity()?.id!, postcards: data }),
    );
    return data;
  });
}

function createScheduledNotification(notification: ScheduledNotification): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.post(`/networks/dashboards/${selectedDashboard}/communication/notification`, notification).then(({ data }) => {
    store.dispatch(
      updateNotification({
        communityId: getSelectedCommunity()?.id!,
        notification: data,
      }),
    );
  });
}

function sendPushNotification(notification: ScheduledNotification): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post(`/networks/dashboards/${selectedDashboard}/communication/notification/direct`, notification)
    .then(({ data }) => {
      store.dispatch(
        updateNotification({
          communityId: getSelectedCommunity()?.id!,
          notification: data,
        }),
      );
    });
}

function createScheduledEmail(email: ScheduledEmail, isRecurring: boolean = false): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post<ScheduledEmail[]>(`/networks/dashboards/${selectedDashboard}/communication/email`, { ...email, isRecurring })
    .then(({ data }) => {
      data.map((e) =>
        store.dispatch(
          updateEmail({
            communityId: getSelectedCommunity()?.id!,
            email: e,
          }),
        ),
      );
    });
}

function sendEmail(email: ScheduledEmail, isRecurring: boolean = false): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .post<
      ScheduledEmail[]
    >(`/networks/dashboards/${selectedDashboard}/communication/email/direct`, _.omit({ ...email, isRecurring }, 'sendDate'))
    .then(({ data }) => {
      data.map((e) =>
        store.dispatch(
          updateEmail({
            communityId: getSelectedCommunity()?.id!,
            email: e,
          }),
        ),
      );
    });
}

function deleteScheduledNotification(notificationId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.delete(`/networks/dashboards/${selectedDashboard}/communication/notification/${notificationId}`).then(() => {
    store.dispatch(
      deleteNotification({ communityId: getSelectedCommunity()?.id!, notificationId }),
    );
  });
}

function deleteScheduledEmail(emailId: number): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService.delete(`/networks/dashboards/${selectedDashboard}/communication/email/${emailId}`).then(() => {
    store.dispatch(deleteEmail({ communityId: getSelectedCommunity()?.id!, emailId }));
  });
}

function updateScheduledNotification(notification: ScheduledNotification): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .put<ScheduledNotification>(`/networks/dashboards/${selectedDashboard}/communication/notification/${notification.communityId}`)
    .then(({ data }) => {
      store.dispatch(
        updateNotification({
          communityId: getSelectedCommunity()?.id!,
          notification: data,
        }),
      );
    });
}

function updateScheduledEmail(email: ScheduledEmail): Promise<void> {
  const { selectedDashboard } = (store.getState() as RootState).application;
  return httpService
    .put<ScheduledEmail>(`/networks/dashboards/${selectedDashboard}/communication/email/${email.emailId}`)
    .then(({ data }) => {
      store.dispatch(
        updateEmail({
          communityId: getSelectedCommunity()?.id!,
          email: data,
        }),
      );
    });
}

export default {
  getEmails,
  getNotifications,
  createScheduledNotification,
  createScheduledEmail,
  deleteScheduledNotification,
  deleteScheduledEmail,
  updateScheduledNotification,
  updateScheduledEmail,
  getPostcards,
  sendEmail,
  sendPushNotification,
};
